import { createContext, useContext, useEffect, useMemo, useReducer } from "react";
import { getItem } from "../data/catalog";
import { cartCount, cartReducer, linePieces, loadCart, saveCart, selectionQty } from "../utils/cart";
import { priceForPieces, savingsForPieces } from "../utils/pricing";

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const [cart, dispatch] = useReducer(cartReducer, undefined, loadCart);

  useEffect(() => saveCart(cart), [cart]);

  const rows = useMemo(
    () =>
      Object.values(cart)
        .sort((a, b) => (b.addedAt || 0) - (a.addedAt || 0))
        .map((line) => {
          const item = getItem(line.id);
          const pieces = linePieces(line);
          return {
            line,
            item,
            pieces,
            total: priceForPieces(item, pieces),
            savings: savingsForPieces(item, pieces),
          };
        }),
    [cart],
  );

  const value = useMemo(
    () => ({
      cart,
      rows,
      count: cartCount(cart),
      total: rows.reduce((sum, r) => sum + r.total, 0),
      savings: rows.reduce((sum, r) => sum + r.savings, 0),
      piecesOf: (id) => linePieces(cart[id]),
      selectionQtyOf: (id, name) => selectionQty(cart, id, name),
      setQuantity: (item, qty) => dispatch({ type: "setQuantity", item, qty }),
      addSelection: (item, name, amount) => dispatch({ type: "addSelection", item, name, amount }),
      removeSelection: (id, name) => dispatch({ type: "removeSelection", id, name }),
      removeItem: (id) => dispatch({ type: "remove", id }),
    }),
    [cart, rows],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const value = useContext(CartContext);
  if (!value) throw new Error("useCart must be used inside <CartProvider>");
  return value;
}
